import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex, Grid, Message, P, Tag } from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import iconix from '@decisiv/iconix';
import RadioGroup from '@decisiv/ui-components/lib/components/RadioGroup';

import isEmpty from 'lodash/isEmpty';
import { LocationOption } from './styles';
import NoLocationsFound from '../NoLocationsFound';
import Separator from '../../../components/Separator';
import ConfiguredIntegrationsApi from '../../../api/configured_integrations';

export default function LocationsResult({
  integration,
  locations,
  selectedLocationId,
  setSelectedLocationId,
  filterSearch,
}) {
  const [selectedValue, setSelectedValue] = useState(selectedLocationId);
  const [validationCache, setValidationCache] = useState({});
  const [locationValidations, setLocationValidations] = useState({});
  const [validationsLoaded, setValidationsLoaded] = useState(false);

  const validServiceNetwork = useCallback(
    (location) => {
      if (isEmpty(integration?.attributes?.service_network)) {
        return true;
      }

      // Return true if pricing is still not serving the necessary data for validation
      if (
        location?.attributes?.service_networks === null ||
        location?.attributes?.service_networks === undefined
      ) {
        return true;
      }

      if (
        Array.isArray(location?.attributes?.service_networks) &&
        isEmpty(location?.attributes?.service_networks)
      ) {
        return false;
      }

      const integrationServiceNetworks =
        integration?.attributes?.service_network.map((item) =>
          item.toLowerCase(),
        );
      const locationServiceNetworks =
        location?.attributes?.service_networks.map((item) =>
          item.name.toLowerCase(),
        );

      // Check if at least one item from locationServiceNetworks is the same as any integrationServiceNetworks;
      return locationServiceNetworks.some((network) =>
        integrationServiceNetworks.includes(network),
      );
    },
    [integration],
  );

  /* istanbul ignore next */
  const validAudience = useCallback(
    (location) => {
      if (isEmpty(integration?.attributes?.audience)) {
        return true;
      }

      if (isEmpty(location?.attributes?.account_type)) {
        return false;
      }

      let locationTypeMapped;

      switch (location.attributes?.account_type.toLowerCase()) {
        case 'fleet':
          locationTypeMapped = 'Asset Management';
          break;
        default:
          locationTypeMapped = 'Service Management';
          break;
      }

      return integration?.attributes?.audience.includes(locationTypeMapped);
    },
    [integration],
  );

  /* istanbul ignore next */
  const validIsConfigured = useCallback(
    async (location) => {
      const srmAccountId = location.attributes.srm_account_id;

      // Check cache first
      if (validationCache[srmAccountId] !== undefined) {
        return validationCache[srmAccountId];
      }

      try {
        const result =
          await ConfiguredIntegrationsApi.fetchConfiguredIntegrations(
            integration.id,
            1,
            25,
            { search: srmAccountId },
          );

        const isValid = result.totalRecords === 0; // Valid if NO existing configurations found

        // Cache the result
        setValidationCache((prev) => ({
          ...prev,
          [srmAccountId]: isValid,
        }));

        return isValid;
      } catch (error) {
        console.error('Validation error:', error);
        return false; // Default to invalid on error
      }
    },
    [integration, validationCache],
  );

  const invalidServiceNetworkAndAudience = (location) => {
    const error =
      locationValidations[location.attributes.srm_account_id] || 'none';
    return error !== 'none';
  };

  useEffect(() => {
    setSelectedLocationId(selectedValue);
  }, [selectedValue]);

  const validateLocations = useCallback(async () => {
    const validationPromises = locations.map(async (location) => {
      const isServiceNetworkValid = validServiceNetwork(location);
      const isAudienceValid = validAudience(location);
      const isConfiguredValid = await validIsConfigured(location);

      if (!isServiceNetworkValid && !isAudienceValid) {
        return {
          locationId: location.attributes.srm_account_id,
          error: 'both',
        };
      }
      if (!isConfiguredValid) {
        return {
          locationId: location.attributes.srm_account_id,
          error: 'invalid_is_configured',
        };
      }
      if (!isServiceNetworkValid) {
        return {
          locationId: location.attributes.srm_account_id,
          error: 'invalid_service_network',
        };
      }
      if (!isAudienceValid) {
        return {
          locationId: location.attributes.srm_account_id,
          error: 'invalid_audience',
        };
      }
      return {
        locationId: location.attributes.srm_account_id,
        error: 'none',
      };
    });

    const results = await Promise.all(validationPromises);
    const validations = results.reduce((acc, { locationId, error }) => {
      acc[locationId] = error;
      return acc;
    }, {});

    setLocationValidations(validations);
    setValidationsLoaded(true);
  }, [locations, validServiceNetwork, validAudience, validIsConfigured]);

  useEffect(() => {
    if (locations.length > 0) {
      validateLocations();
    }
  }, [validateLocations]);

  const renderErrorTag = (location) => {
    const error =
      locationValidations[location.attributes.srm_account_id] || 'none';

    return (
      <>
        {error === 'invalid_is_configured' && (
          <Tag
            color="warning"
            size="medium"
            variant="outline"
            text={t`Already Configured`}
            icon={iconix.ExclamationTriangle}
          />
        )}
        {error === 'invalid_service_network' && (
          <Tag
            color="warning"
            size="medium"
            variant="outline"
            text={t`Out of Network`}
            icon={iconix.ExclamationTriangle}
          />
        )}
        {error === 'invalid_audience' && (
          <Tag
            color="warning"
            size="medium"
            variant="outline"
            text={t`Out of Audience`}
            icon={iconix.ExclamationTriangle}
          />
        )}
        {error === 'both' && (
          <Tag
            color="warning"
            size="medium"
            variant="outline"
            text={t`Out of Network and Audience`}
            icon={iconix.ExclamationTriangle}
          />
        )}
      </>
    );
  };

  const renderErrorMsg = (location) => {
    const error =
      locationValidations[location.attributes.srm_account_id] || 'none';

    return (
      <>
        {error === 'invalid_is_configured' && (
          <Message icon={iconix.ExclamationTriangle} color="warning">
            <Trans>
              Cannot add location. Location is already assigned to this
              integration.
            </Trans>
          </Message>
        )}
        {error === 'invalid_service_network' && (
          <Message icon={iconix.ExclamationTriangle} color="warning">
            <Trans>
              Cannot add location. This location is outside the
              integration&apos;s service network.
            </Trans>
          </Message>
        )}
        {error === 'invalid_audience' && (
          <Message icon={iconix.ExclamationTriangle} color="warning">
            <Trans>
              Cannot add location. This location is outside the
              integration&apos;s audience.
            </Trans>
          </Message>
        )}
        {error === 'both' && (
          <Message icon={iconix.ExclamationTriangle} color="warning">
            <Trans>
              Cannot add location. This location is outside the
              integration&apos;s service network and audience.
            </Trans>
          </Message>
        )}
      </>
    );
  };

  if (locations.length === 0) {
    return <NoLocationsFound filterSearch={filterSearch} />;
  }

  if (!validationsLoaded) {
    return (
      <Flex justifyContent="center" paddingY={2}>
        <Trans>
          <P color="alaskanHusky">Validating locations...</P>
        </Trans>
      </Flex>
    );
  }

  return locations.map((location) => (
    <div key={location.id}>
      <LocationOption
        paddingY={1}
        marginX={0}
        alignment={['start', 'top']}
        selected={selectedLocationId === location.attributes.srm_account_id}
      >
        <Grid.Column>
          <Grid.Row>
            <Grid.Column>
              <Flex justifyContent="space-between">
                <RadioGroup
                  name="radio-add-location"
                  onChange={(e) => {
                    setSelectedValue(e.target.value);
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  disabled={invalidServiceNetworkAndAudience(location)}
                  items={[
                    {
                      label: `${location.attributes.name} (${location.attributes.srm_account_id})`,
                      value: location.attributes.srm_account_id,
                    },
                  ]}
                  value={
                    selectedLocationId === location.attributes.srm_account_id
                      ? selectedLocationId
                      : ''
                  }
                />
                <Flex>{renderErrorTag(location)}</Flex>
              </Flex>
            </Grid.Column>
          </Grid.Row>
          <Flex
            alignItems="flexStart"
            marginTop={0.5}
            marginLeft={3}
            flexWrap="wrap"
          >
            <P as="span" color="alaskanHusky" size="small">
              {location.oem}
            </P>
            <Separator />
            <P as="span" color="alaskanHusky" size="small">
              {location.attributes.account_type}
            </P>
            <Separator />
            <P as="span" color="alaskanHusky" marginLeft={0.5} size="small">
              {location.attributes.city}, {location.attributes.state}
            </P>
          </Flex>
        </Grid.Column>
      </LocationOption>
      <Flex paddingY={1}>{renderErrorMsg(location)}</Flex>
    </div>
  ));
}

LocationsResult.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  selectedLocationId: PropTypes.string.isRequired,
  setSelectedLocationId: PropTypes.func.isRequired,
  filterSearch: PropTypes.string.isRequired,
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      oem: PropTypes.string,
      type: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
    }),
  ).isRequired,
};
